import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Send, Trash2, Pencil, AtSign, Paperclip, RotateCw, Check, CheckCheck, FileText, AlertTriangle, ExternalLink, Flame, MessageSquare, Bell, Reply, X } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AvatarBlock } from "@/components/settings/AccountSection";
import UserAvatar from "@/components/ui/UserAvatar";
import MentionText from "@/components/tasks/MentionText";
import FileOrScanDialog from "@/components/common/FileOrScanDialog";
import ConfirmDestructiveDialog from "@/components/common/ConfirmDestructiveDialog";
import { notifyMessagesRead } from "@/lib/unreadMessages";
import api from "@/lib/api";
import { selectableUsers } from "@/lib/userDirectory";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 2000;
const YELLOW = "#F9BC26";  // Tâches module accent

// Detects system messages generated by /redo (see backend/task_routes.py).
// Rendered as an orange bordered card in the chat so it can never be
// visually mistaken for the red "Refusée" task status. Spec 2026-02-15.
const REDO_PATTERN = /^🔄\s*Refaire\s*—/i;
// iter410 L2-8 (Q2) — type de bulle : couleur du liseré + icône près de l'heure.
export const TYPE = {
  discussion: { color: "#F5C518", icon: MessageSquare, label: "Discussion" },
  relance: { color: "#E0569B", icon: Bell, label: "Relance" },
  urgent: { color: "#F97316", icon: Flame, label: "Urgent" },
};

// -------- Notification "pop" sound (spec 2026-02-15) ----------------------
// Uses the Web Audio API so we don't ship an asset. Two very short sine
// blips (1000 Hz → 1400 Hz) with a fast decay — under 200 ms total. Safe to
// call: creates & tears down its own AudioContext each time so browsers'
// autoplay policies don't fight it.
let _sharedAudioCtx = null;
function playPop() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_sharedAudioCtx) _sharedAudioCtx = new AC();
    const ctx = _sharedAudioCtx;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    gain.connect(ctx.destination);
    const o1 = ctx.createOscillator(); o1.type = "sine"; o1.frequency.value = 1000;
    o1.connect(gain); o1.start(now); o1.stop(now + 0.08);
    const o2 = ctx.createOscillator(); o2.type = "sine"; o2.frequency.value = 1400;
    o2.connect(gain); o2.start(now + 0.05); o2.stop(now + 0.16);
  } catch { /* muted browsers etc. — silently swallow */ }
}

/**
 * Chat integrated in the task detail page.
 *   • 2s polling for messages + typing indicator
 *   • Author can edit within a 5-min window; admins can delete anytime.
 *   • @mentions: type "@" to open the users dropdown; only users involved
 *     in the task (creator + assignees) are listed.
 *   • Message bubbles use the Tâches module accent (yellow) instead of
 *     the previous Vertec-blue — matches the rest of the detail page.
 *   • A 📎 button in the composer uploads to the same task-attachments
 *     endpoint used by <AttachmentsPanel>. Uploads notify via toast; the
 *     panel will pick them up on its own next refresh.
 */
export default function Chat({ task, users, currentTaskAssignments, highlightMessageId, frozen = false, frozenBanner = null, onMessagesChange }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  useEffect(() => { onMessagesChange?.(messages); }, [messages, onMessagesChange]);
  const [body, setBody] = useState("");
  const [typing, setTyping] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [pendingMentions, setPendingMentions] = useState([]);
  // Spec iter212 (2026-02-28) — remplace `window.confirm` par ConfirmDestructiveDialog.
  const [msgToDelete, setMsgToDelete] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  // Spec iter97 (Feb 2026) — toggle « Urgent » du composer. Éphémère :
  // se remet à false après chaque envoi (pas de persistance).
  const [urgent, setUrgent] = useState(false);
  // iter410 L2-8 — réponse façon WhatsApp (message d'origine cité ; la réponse hérite de son type).
  const [replyTo, setReplyTo] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const listRef = useRef(null);
  const taRef = useRef(null);
  const lastMsgIdsRef = useRef(new Set());
  const hasLoadedRef = useRef(false);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Poll messages + typing
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const [{ data: msgs }, { data: t }] = await Promise.all([
          api.get(`/tasks/${task.id}/messages`),
          api.get(`/tasks/${task.id}/typing`),
        ]);
        if (stop) return;
        setMessages(msgs);
        setTyping((t.users || []).filter((tu) => tu.id !== user?.id));

        // Detect brand-new messages by others → play discreet pop.
        // Skip the very first load (avoid a burst on page open) and skip
        // the user's own outgoing messages. Uses the freshly-fetched IDs
        // to diff against the previous poll.
        const known = lastMsgIdsRef.current;
        if (hasLoadedRef.current && user?.notification_sounds !== false) {
          const fresh = (msgs || []).filter(
            (m) => !known.has(m.id) && m.author_id !== user?.id && !m.is_deleted
          );
          if (fresh.length > 0) playPop();
        }
        lastMsgIdsRef.current = new Set((msgs || []).map((m) => m.id));
        hasLoadedRef.current = true;

        // Fire-and-forget: mark everything as read on my side.
        api.post(`/tasks/${task.id}/messages/read-all`).then((r) => { if (r?.data?.marked) notifyMessagesRead(); }).catch(() => {});
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [task.id, user?.id, user?.notification_sounds]);

  // Scroll on new message OR jump-to-highlight target
  useEffect(() => {
    if (!listRef.current) return;
    if (highlightMessageId) {
      const el = listRef.current.querySelector(`[data-testid="chat-msg-${highlightMessageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("chat-highlight-pulse");
        setTimeout(() => el.classList.remove("chat-highlight-pulse"), 1500);
        return;
      }
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, highlightMessageId]);

  // Fire typing pings + detect @mention query --------------------------------
  const lastPingRef = useRef(0);
  const onType = (v) => {
    setBody(v);
    const now = Date.now();
    if (now - lastPingRef.current > 1500) {
      lastPingRef.current = now;
      api.post(`/tasks/${task.id}/typing`).catch(() => {});
    }
    const cursor = taRef.current?.selectionStart ?? v.length;
    const prefix = v.slice(0, cursor);
    const m = prefix.match(/(?:^|\s)@(\w{0,20})$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    const involvedIds = new Set([task.created_by, ...(currentTaskAssignments || []).map((a) => a.user_id), ...messages.map((m) => m.author_id)]);
    const pool = selectableUsers(users).filter((u) => involvedIds.has(u.id) && u.id !== user?.id);
    const q = mentionQuery;
    return pool
      .filter((u) => (`${u.first_name} ${u.last_name}`.toLowerCase()).includes(q))
      .slice(0, 6);
  }, [mentionQuery, users, task, currentTaskAssignments, messages, user]);

  const pickMention = (u) => {
    setBody((prev) => prev.replace(/@(\w{0,20})$/, `@${u.first_name}${u.last_name?.[0] || ""} `));
    setPendingMentions((prev) => (prev.includes(u.id) ? prev : [...prev, u.id]));
    setMentionQuery(null);
    taRef.current?.focus();
  };

  // Send message --------------------------------------------------------------
  const send = async () => {
    const v = body.trim();
    if (!v) return;
    // Spec iter97 (Feb 2026) — on capture le flag urgent AVANT l'envoi
    // (immutabilité) puis on le remet à false sitôt le POST parti.
    const isUrgent = urgent;
    try {
      const { data } = await api.post(`/tasks/${task.id}/messages`, {
        body: v, mentions: pendingMentions, urgent: isUrgent, reply_to: replyTo?.id || null,
      });
      setMessages((m) => [...m, data]);
      setBody(""); setPendingMentions([]); setReplyTo(null);
      // Reset systématique du toggle après envoi — jamais rémanent.
      setUrgent(false);
      if (isUrgent) {
        toast.success("Message urgent envoyé — email en cours d'expédition aux participants");
      }
    } catch (err) {
      toast.error("Envoi impossible", { description: err.response?.data?.detail || "" });
    }
  };

  // Upload attachment ---------------------------------------------------------
  const chatUploader = async (file, { onProgress, signal }) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/tasks/${task.id}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress?.((e.loaded / (e.total || file.size)) * 100, e.loaded),
      signal,
    });
  };

  // Edit / delete a message ---------------------------------------------------
  const startEdit = (m) => { setEditingId(m.id); setEditBody(m.body); };
  const saveEdit = async () => {
    const v = editBody.trim();
    if (!v) return;
    try {
      await api.patch(`/tasks/${task.id}/messages/${editingId}`, { body: v, mentions: [] });
      setMessages((prev) => prev.map((m) => (m.id === editingId ? { ...m, body: v, updated_at: new Date().toISOString() } : m)));
      setEditingId(null); setEditBody("");
    } catch (err) {
      toast.error("Modification refusée", { description: err.response?.data?.detail || "" });
    }
  };
  const removeMsg = async (m) => {
    // Spec iter212 — ouverture du dialogue de confirmation stylé.
    setMsgToDelete(m);
  };
  const performRemoveMsg = async () => {
    if (!msgToDelete) return;
    try {
      await api.delete(`/tasks/${task.id}/messages/${msgToDelete.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== msgToDelete.id));
      setMsgToDelete(null);
    } catch (err) {
      toast.error("Suppression refusée", { description: err.response?.data?.detail || "" });
    }
  };

  const isAdmin = user?.role === "admin";
  const jumpTo = (id) => {
    const el = listRef.current?.querySelector(`[data-testid="chat-msg-${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("chat-highlight-pulse");
    setTimeout(() => el.classList.remove("chat-highlight-pulse"), 1500);
  };
  const withinEditWindow = (m) => (Date.now() - new Date(m.created_at).getTime()) < 5 * 60 * 1000;

  return (
    <div className="flex flex-col h-[520px] rounded-md border border-[#3A3A3A] bg-white/[0.02] shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
      {/* Messages list -------------------------------------------------------- */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-list">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-10">Aucun message pour le moment.</div>
        )}
        {messages.map((m) => {
          const isSystem = (m.kind || "user") === "system_status";
          if (isSystem) {
            return <SystemStatusBanner key={m.id} msg={m} />;
          }
          const author = usersById[m.author_id];
          const mine = m.author_id === user?.id;
          const canEdit = !frozen && mine && withinEditWindow(m) && m.msg_type !== "relance";
          const canDelete = !frozen && (isAdmin || canEdit) && m.msg_type !== "relance";
          const type = TYPE[m.msg_type] || TYPE.discussion;
          const TypeIcon = type.icon;
          const quoted = m.reply_to ? messages.find((x) => x.id === m.reply_to) : null;
          const quotedAuthor = quoted ? usersById[quoted.author_id] : null;
          const quotedType = quoted ? (TYPE[quoted.msg_type] || TYPE.discussion) : null;
          const isRedo = REDO_PATTERN.test(m.body);
          const accent = isRedo ? "#F97316" : type.color;
          return (
            <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`} data-msg-type={m.msg_type || "discussion"} onMouseEnter={() => setHoverId(m.id)} onMouseLeave={() => setHoverId((h) => (h === m.id ? null : h))}>
              <AvatarBlock
                initials={`${author?.first_name?.[0] || ""}${author?.last_name?.[0] || ""}`}
                size={32}
                seed={author?.id || author?.email}
                name={`${author?.first_name || ""} ${author?.last_name || ""}`.trim() || undefined}
              />
              <div className={`max-w-[70%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="text-[11px] text-muted-foreground mb-0.5 inline-flex items-center gap-1.5">
                  <span className="font-medium" style={{ color: "#B4B4BC" }}>{`${author?.first_name || ""} ${author?.last_name || ""}`.trim() || "Utilisateur"}</span>
                  <span className="opacity-40">·</span>
                  {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  <span className="inline-flex items-center gap-0.5" style={{ color: type.color }} title={type.label} data-testid={`chat-type-${m.id}`}>
                    <TypeIcon size={11} strokeWidth={2.4} />
                    {m.msg_type === "relance" && m.relance_n ? <span className="tabular-nums">n° {m.relance_n}</span> : null}
                  </span>
                  {m.updated_at && <span className="ml-1 italic">(modifié)</span>}
                </div>

                {editingId === m.id ? (
                  <div className="flex flex-col gap-2">
                    <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2} className="text-sm" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Annuler</Button>
                      <Button size="sm" className="text-[#1B1B1B]" style={{ background: YELLOW }} onClick={saveEdit}>Enregistrer</Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="inline-block px-3 py-2 text-sm whitespace-pre-wrap text-left"
                    style={{ background: "#222227", borderRadius: mine ? "14px 4px 4px 14px" : "4px 14px 14px 4px", color: "#EDEDED", [mine ? "borderRight" : "borderLeft"]: `3px solid ${accent}` }}
                    data-testid={isRedo ? `chat-redo-${m.id}` : `chat-bubble-${m.id}`}
                  >
                    {quoted && (
                      <button type="button" onClick={() => jumpTo(quoted.id)} className="block w-full text-left mb-1.5 px-2 py-1 rounded-md" style={{ background: "rgba(0,0,0,.28)", borderLeft: `3px solid ${quotedType.color}` }} data-testid={`chat-quote-${m.id}`}>
                        <div className="text-[10.5px] font-semibold" style={{ color: quotedType.color }}>{`${quotedAuthor?.first_name || ""} ${quotedAuthor?.last_name || ""}`.trim() || "Utilisateur"}</div>
                        <div className="text-[11.5px] truncate" style={{ color: "#B4B4BC", maxWidth: 360 }}>{quoted.body}</div>
                      </button>
                    )}
                    {isRedo && <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#F97316" }}><RotateCw size={11} strokeWidth={2.8} /> Refaire</div>}
                    <MentionText text={isRedo ? m.body.replace(/^🔄\s*Refaire\s*—\s*motif\s*:\s*/i, "").trim() : m.body} />
                  </div>
                )}

                {editingId !== m.id && (
                  <div className={`mt-1 flex gap-3 text-xs items-center ${mine ? "justify-end" : ""}`} style={{ minHeight: 16 }}>
                    {!frozen && hoverId === m.id && (
                      <button onClick={() => { setReplyTo(m); taRef.current?.focus(); }} className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity" style={{ color: "#8CC4FF" }} data-testid={`chat-reply-${m.id}`}>
                        <Reply size={11} strokeWidth={2.2} /> Répondre
                      </button>
                    )}
                    {mine && (
                      canEdit ? (
                        <button
                          onClick={() => startEdit(m)}
                          className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                          style={{ color: YELLOW }}
                          data-testid={`chat-edit-${m.id}`}
                        >
                          <Pencil size={11} strokeWidth={2.2} /> Modifier
                        </button>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 opacity-40 cursor-not-allowed"
                          title="La modification n'est possible que dans les 5 minutes suivant l'envoi."
                          data-testid={`chat-edit-locked-${m.id}`}
                        >
                          <Pencil size={11} /> Modifier
                        </span>
                      )
                    )}
                    {canDelete && (
                      <button
                        onClick={() => removeMsg(m)}
                        className="text-muted-foreground hover:text-status-danger inline-flex items-center gap-1 transition-colors"
                        data-testid={`chat-delete-${m.id}`}
                      >
                        <Trash2 size={11} /> Supprimer
                      </button>
                    )}
                    {mine && <ReadReceiptTicks message={m} usersById={usersById} />}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Typing indicator (below list, above input) ---------------------------- */}
      {typing.length > 0 && (
        <div className="px-4 py-1.5 text-xs text-muted-foreground italic border-t border-[#3A3A3A]/60" data-testid="chat-typing">
          <span className="inline-flex items-center gap-2">
            <span className="flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-current opacity-60 animate-pulse" />
              <span className="w-1 h-1 rounded-full bg-current opacity-60 animate-pulse" style={{ animationDelay: "150ms" }} />
              <span className="w-1 h-1 rounded-full bg-current opacity-60 animate-pulse" style={{ animationDelay: "300ms" }} />
            </span>
            {typing.map((u) => u.first_name).join(", ")}{" "}
            {typing.length > 1 ? "sont" : "est"} en train d&apos;écrire…
          </span>
        </div>
      )}

      {/* Composer ------------------------------------------------------------- */}
      {frozen ? (frozenBanner || null) : (<>
      {replyTo && (
        <div className="border-t border-[#3A3A3A] px-3 py-1.5 flex items-center gap-2" style={{ background: "rgba(0,0,0,.28)", borderLeft: `3px solid ${(TYPE[replyTo.msg_type] || TYPE.discussion).color}` }} data-testid="chat-reply-banner">
          <Reply size={13} style={{ color: "#8CC4FF" }} />
          <span className="text-[11.5px] min-w-0 flex-1 truncate" style={{ color: "#B4B4BC" }}>
            Répondre à <strong style={{ color: "#E8E8EC" }}>{`${usersById[replyTo.author_id]?.first_name || ""} ${usersById[replyTo.author_id]?.last_name || ""}`.trim() || "Utilisateur"}</strong> · {replyTo.body}
          </span>
          <button type="button" onClick={() => setReplyTo(null)} className="shrink-0 hover:opacity-80" aria-label="Annuler la réponse" data-testid="chat-reply-cancel"><X size={14} style={{ color: "#8A8A93" }} /></button>
        </div>
      )}
      {/* Spec iter97 (Feb 2026) — bandeau d'avertissement au-dessus du composer
          quand le toggle Urgent est actif. */}
      {urgent && (
        <div
          className="border-t border-[#3A3A3A] px-3 py-2 flex items-center gap-2"
          style={{ background: "rgba(224,122,122,0.10)", color: "#FCA5A5" }}
          data-testid="chat-urgent-warning"
        >
          <AlertTriangle size={13} strokeWidth={2.4} />
          <span className="text-[11.5px] font-medium">
            Ce message sera envoyé par <strong>email</strong> au(x) destinataire(s) du chat.
          </span>
        </div>
      )}
      <div
        className={`relative p-2 flex gap-2 items-end ${urgent ? "" : "border-t border-[#3A3A3A]"}`}
        style={urgent ? { borderTop: "1px solid #E07A7A" } : undefined}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-[42px] w-[42px] shrink-0 border-[#3A3A3A] hover:bg-white/5"
          onClick={() => setAttachOpen(true)}
          aria-label="Joindre un fichier"
          title="Joindre un fichier"
          data-testid="chat-attach-btn"
        >
          <Paperclip size={16} style={{ color: YELLOW }} />
        </Button>

        {/* Spec iter97 (Feb 2026) — toggle « Urgent » en pilule. Position :
            juste après le trombone, avant le Textarea. État actif =
            fond rouge sombre, bordure rouge, texte rouge en 700. */}
        <button
          type="button"
          onClick={() => setUrgent((v) => !v)}
          aria-pressed={urgent}
          className="inline-flex items-center gap-1.5 shrink-0 transition-all"
          style={{
            height: 42,
            padding: "0 12px",
            borderRadius: 21,
            background: urgent ? "#3A1414" : "#242424",
            border: `1px solid ${urgent ? "#E07A7A" : "#3A3A3A"}`,
            color: urgent ? "#FCA5A5" : "#9CA3AF",
            fontSize: 12,
            fontWeight: urgent ? 700 : 500,
            letterSpacing: urgent ? "0.02em" : 0,
            cursor: "pointer",
          }}
          title={urgent ? "Urgent activé — cliquez pour annuler" : "Marquer ce message comme urgent (déclenche un email)"}
          data-testid="chat-urgent-toggle"
        >
          <Flame size={14} strokeWidth={2.4} />
          Urgent
        </button>

        <FileOrScanDialog
          open={attachOpen}
          onOpenChange={setAttachOpen}
          uploader={chatUploader}
          title="Joindre au chat"
          testIdPrefix="chat-attach-dialog"
        />

        <Textarea
          ref={taRef}
          value={body}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Écrire un message… (Entrée pour envoyer, Shift+Entrée pour saut de ligne)"
          className="flex-1 min-h-[42px] resize-none"
          style={urgent ? { borderColor: "#E07A7A", boxShadow: "0 0 0 1px rgba(224,122,122,0.35)" } : undefined}
          data-testid="chat-input"
        />

        {mentionMatches.length > 0 && (
          <div
            className="absolute bottom-full left-14 mb-1 w-64 max-h-56 overflow-y-auto rounded-md border border-[#3A3A3A] bg-card shadow-xl z-10"
            data-testid="mention-dropdown"
          >
            {mentionMatches.map((u) => (
              <button
                key={u.id}
                onClick={() => pickMention(u)}
                className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 text-sm"
                data-testid={`mention-option-${u.id}`}
              >
                <AtSign size={12} style={{ color: YELLOW }} />
                <span className="font-medium">{u.first_name} {u.last_name}</span>
              </button>
            ))}
          </div>
        )}

        <Button
          onClick={send}
          disabled={!body.trim()}
          className={`h-[42px] border-0 ${urgent ? "text-white" : "text-[#1B1B1B]"}`}
          style={{ background: urgent ? "#C1401A" : YELLOW }}
          data-testid="chat-send"
        >
          <Send size={16} />
        </Button>
      </div>
      {/* Spec iter212 — Confirmation stylée pour suppression message. */}
      </>)}
      <ConfirmDestructiveDialog
        open={!!msgToDelete}
        onOpenChange={(v) => { if (!v) setMsgToDelete(null); }}
        title="Supprimer ce message ?"
        description="Cette action est irréversible."
        icon={<MessageSquare size={20} />}
        body={msgToDelete ? (
          <div
            className="rounded-md px-3 py-2.5 text-[12.5px]"
            style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.06)", color: "#EDEDED" }}
            data-testid="task-chat-msg-preview"
          >
            <span className="opacity-60">« </span>
            {(msgToDelete.body || msgToDelete.text || "").slice(0, 140)}
            {(msgToDelete.body || msgToDelete.text || "").length > 140 ? "…" : ""}
            <span className="opacity-60"> »</span>
          </div>
        ) : null}
        warningText="Le message ne sera plus visible pour personne dans la discussion."
        confirmLabel="Supprimer"
        onConfirm={performRemoveMsg}
        testId="confirm-delete-task-msg"
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// ReadReceiptTicks — WhatsApp-style read receipts on outgoing messages.
//   ✓  (gray) : sent, not yet read by anyone
//   ✓✓ (blue): read by at least one other user; hover for names + times.
// Spec 2026-02-15 (Lot C · point 2a).
// ---------------------------------------------------------------------------
function ReadReceiptTicks({ message, usersById }) {
  const readBy = message.read_by || {};
  const readers = Object.entries(readBy);
  const seen = readers.length > 0;
  const tooltip = seen
    ? readers
        .map(([uid, at]) => {
          const u = usersById?.[uid];
          const name = u ? `${u.first_name} ${u.last_name}` : "Utilisateur";
          const time = at ? new Date(at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
          return `${name} — ${time}`;
        })
        .join("\n")
    : "Envoyé, pas encore lu";
  return (
    <span
      className="inline-flex items-center"
      style={{ color: seen ? "#0078D4" : "#6B7280", marginLeft: 2 }}
      title={tooltip}
      data-testid={`chat-read-${message.id}`}
    >
      {seen ? <CheckCheck size={14} strokeWidth={2.4} /> : <Check size={13} strokeWidth={2.4} />}
    </span>
  );
}


/**
 * SystemStatusBanner — Bandeau centré rendu pour les messages système
 * générés automatiquement lors d'un changement de statut du document
 * lié (spec 2026-02-XX rev17, Point K). Sans avatar, sans nom d'auteur,
 * non éditable, non supprimable. Distinct des messages utilisateurs et
 * du bandeau orange "Refaire".
 *
 * Couleurs :
 *   • delai_depasse → rouge (#DC2626)
 *   • cloture       → bleu neutre (#0078D4)
 *   • suspendu      → orange terne (#B8623F)
 *   • termine       → violet (#8B5CF6)
 *   • terminee      → vert (#5DCAA5)
 *   • a_refaire     → orange vif (#F97316)
 *   • défaut        → gris
 */
const STATUS_ACCENT = {
  delai_depasse: "#DC2626",
  cloture:       "#0078D4",
  suspendu:      "#B8623F",
  termine:       "#8B5CF6",
  terminee:      "#5DCAA5",
  a_refaire:     "#F97316",
};

function SystemStatusBanner({ msg }) {
  const meta = msg.system_meta || {};
  const newStatus = meta.new_status || "";
  const accent = STATUS_ACCENT[newStatus] || "#6B7280";
  const isOverdue = newStatus === "delai_depasse";
  const Icon = isOverdue ? AlertTriangle : FileText;
  const timeStr = new Date(msg.created_at).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateStr = new Date(msg.created_at).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
  });

  return (
    <div
      className="flex justify-center my-2"
      data-testid={`chat-system-${msg.id}`}
    >
      <div
        className="max-w-[85%] rounded-md px-3 py-2 text-sm"
        style={{
          background: `${accent}18`,
          border: `1px solid ${accent}66`,
          color: "#E5E7EB",
          boxShadow: `0 2px 6px ${accent}22`,
        }}
      >
        <div
          className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1"
          style={{ color: accent }}
        >
          <Icon size={11} strokeWidth={2.8} />
          {isOverdue ? "Document en délai dépassé" : "Changement de statut — document lié"}
        </div>
        <div className="text-center whitespace-pre-wrap leading-relaxed">
          <div className="font-medium">
            {meta.document_id ? (
              <RouterLink
                to={`/documents/${meta.document_id}`}
                data-testid={`chat-system-doclink-${msg.id}`}
                className="inline-flex items-center gap-1 hover:underline transition-colors"
                style={{ color: "#F9BC26" }}
                title="Ouvrir le document lié"
              >
                📄 <span>{meta.document_title || "Document"}</span>
                {meta.document_ref && (
                  <span className="text-muted-foreground text-xs ml-1">
                    ({meta.document_ref})
                  </span>
                )}
                <ExternalLink size={11} strokeWidth={2.4} className="ml-0.5 opacity-70" />
              </RouterLink>
            ) : (
              <>
                📄 <span style={{ color: "#F9BC26" }}>{meta.document_title || "Document"}</span>
                {meta.document_ref && (
                  <span className="text-muted-foreground text-xs ml-1">({meta.document_ref})</span>
                )}
              </>
            )}
          </div>
          {isOverdue ? (
            <div className="mt-1 text-xs" style={{ color: accent, fontWeight: 600 }}>
              ⚠️ Statut : {meta.new_label || "Délai dépassé"}
            </div>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground">
              Statut :{" "}
              <span style={{ color: "#9CA3AF" }}>{meta.prev_label || "—"}</span>
              {" → "}
              <span style={{ color: accent, fontWeight: 600 }}>
                {meta.new_label || "—"}
              </span>
            </div>
          )}
          {meta.reason && (
            <div className="mt-1 text-xs italic text-muted-foreground">
              Motif : {meta.reason}
            </div>
          )}
          <div className="mt-1 text-[11px] text-muted-foreground inline-flex items-center gap-1.5 justify-center">
            {meta.actor_name ? (
              <>
                <span>— par</span>
                <UserAvatar
                  user={{
                    id: meta.actor_id || meta.actor_name,
                    first_name: meta.actor_name.split(" ")[0] || "",
                    last_name: meta.actor_name.split(" ").slice(1).join(" ") || "",
                  }}
                  size={16}
                  subtitle="Auteur du changement de statut"
                />
                <span className="opacity-40">·</span>
              </>
            ) : null}
            {dateStr} · {timeStr}
          </div>
        </div>
      </div>
    </div>
  );
}
